import { SiteHeader } from "@/components/site-header"
import { cn } from "@/lib/utils"

// ─── Componentes auxiliares ───────────────────────────────────────────────────

type AlertLevel = "alert" | "warning" | "critical" | "none"

const LEVEL_CONFIG: Record<AlertLevel, { label: string; sublabel: string; color: string; dot: string }> = {
  none:     { label: "Sin alerta",    sublabel: "normal",   color: "bg-muted text-muted-foreground border border-border",          dot: "bg-slate-300" },
  alert:    { label: "Atención",      sublabel: "amarillo", color: "bg-yellow-100 text-yellow-800 border border-yellow-300",        dot: "bg-yellow-400" },
  warning:  { label: "Demorado",      sublabel: "naranja",  color: "bg-orange-100 text-orange-800 border border-orange-300",        dot: "bg-orange-400" },
  critical: { label: "Crítico",       sublabel: "rojo",     color: "bg-red-100    text-red-800    border border-red-300",           dot: "bg-red-500" },
}

function LevelBadge({ level }: { level: AlertLevel }) {
  const { label, sublabel, color, dot } = LEVEL_CONFIG[level]
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full pl-1.5 pr-2.5 py-0.5", color)}>
      <span className={cn("size-1.5 rounded-full shrink-0", dot)} />
      <span className="text-[11px] font-semibold">{label}</span>
      <span className="text-[10px] opacity-60">({sublabel})</span>
    </span>
  )
}

function StatusDot({ color }: { color: string }) {
  return <span className={cn("inline-block size-2 rounded-full shrink-0", color)} />
}

function Code({ children }: { children: string }) {
  return <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{children}</code>
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold border-b pb-2">{title}</h2>
      {children}
    </section>
  )
}

function Row({ cond, desc, level }: { cond: string; desc: string; level: AlertLevel }) {
  return (
    <tr>
      <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{cond}</td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground">{desc}</td>
      <td className="px-3 py-2.5"><LevelBadge level={level} /></td>
    </tr>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShipmentAlertsPage() {
  return (
    <>
      <SiteHeader title="Alertas de Envíos — shipment-alerts" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-4xl space-y-12">

          {/* ── Intro ── */}
          <Section title="¿Qué es esto?">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <Code>packages/shared/src/utils/shipment-alerts.ts</Code> centraliza la lógica de
              alertas visuales de envíos. Es una función pura, sin dependencias de framework,
              usable en la API, el web y la app mobile.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Determina si una card del Kanban (u otro componente) debe mostrar un color de urgencia
              según el estado del envío y sus timestamps — sin duplicar lógica en cada vista.
            </p>
          </Section>

          {/* ── Niveles ── */}
          <Section title="Niveles de alerta">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(["none", "alert", "warning", "critical"] as AlertLevel[]).map(level => {
                const { label, sublabel, color, dot } = LEVEL_CONFIG[level]
                const descs: Record<AlertLevel, string> = {
                  none:     "El envío no tiene ningún problema de tiempo detectado. La card se muestra con fondo normal.",
                  alert:    "La fecha proyectada se acerca en menos de 2 horas. Momento de verificar que el camión esté en camino.",
                  warning:  "Pasó la fecha proyectada pero dentro del margen de 2 horas. Contactar al chofer para confirmar situación.",
                  critical: "Más de 2 horas sin registrar el evento esperado. Requiere intervención inmediata.",
                }
                return (
                  <div key={level} className={cn("rounded-lg border p-3 space-y-1", color)}>
                    <div className="flex items-center gap-2">
                      <span className={cn("size-2.5 rounded-full shrink-0", dot)} />
                      <span className="font-semibold text-sm">{label}</span>
                      <span className="text-[10px] opacity-50 font-mono">({sublabel})</span>
                    </div>
                    <p className="text-xs leading-relaxed pl-4 opacity-80">{descs[level]}</p>
                  </div>
                )
              })}
            </div>
          </Section>

          {/* ── Reglas por estado ── */}
          <Section title="Reglas por estado">

            {/* planificado */}
            <div className="rounded-lg border overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 dark:bg-blue-950/20 border-b">
                <StatusDot color="bg-blue-500" />
                <span className="text-sm font-semibold">Planificado</span>
                <span className="text-xs text-muted-foreground ml-1">— esperando confirmación del operador</span>
              </div>
              <div className="px-3 py-2.5 border-b bg-muted/20">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mide cuánto tiempo lleva el envío en estado <strong>Planificado</strong> sin avanzar a <strong>Activo</strong>.
                  Usa el campo <Code>plannedAt</Code> — se setea automáticamente la primera vez que el envío entra en este estado
                  y <strong>nunca se modifica</strong> aunque se editen otros campos del registro.
                </p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Tiempo esperando</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Situación</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Alerta</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <Row cond="< 15 min"        desc="Recién planificado, tiempo normal de procesamiento."                                       level="none" />
                  <Row cond="15 min – 30 min" desc="Lleva más de 15 minutos sin confirmarse. Conviene verificar disponibilidad del chofer."    level="alert" />
                  <Row cond="30 min – 60 min" desc="Media hora sin confirmar. El operador debe contactar al chofer o reasignar el viaje."      level="warning" />
                  <Row cond="> 60 min"        desc="Más de una hora parado sin confirmar. Requiere intervención inmediata."                    level="critical" />
                </tbody>
              </table>
            </div>

            {/* activo */}
            <div className="rounded-lg border overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-950/20 border-b">
                <StatusDot color="bg-emerald-500" />
                <span className="text-sm font-semibold">Activo</span>
                <span className="text-xs text-muted-foreground ml-1">— camión en tránsito o esperando carga en origen</span>
              </div>
              <div className="px-3 py-2.5 border-b bg-muted/20">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  El estado <strong>Activo</strong> tiene <strong>dos reglas independientes</strong>. La de mayor severidad prevalece.
                </p>
              </div>

              {/* Regla A */}
              <div className="px-3 py-2 border-b bg-muted/10">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Regla A — llegada proyectada a origen</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Compara la <strong>llegada proyectada a origen</strong> (<Code>scheduledOriginArrivalAt</Code>)
                  contra la <strong>llegada real registrada</strong> (<Code>originArrivalAt</Code>).
                  En cuanto se registra la llegada real, esta regla desaparece automáticamente.
                </p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Tiempo vs. llegada proyectada (T)</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Situación</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Alerta</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <Row cond="Faltan más de 2h para T"  desc="El camión tiene tiempo suficiente para llegar al origen."                              level="none" />
                  <Row cond="Faltan menos de 2h para T" desc="Hora de verificar que el camión esté en ruta y sin inconvenientes."                    level="alert" />
                  <Row cond="Pasó T, dentro de 2h"     desc="El camión aún no llegó pero está dentro del margen tolerable. Contactar al chofer."    level="warning" />
                  <Row cond="Pasaron más de 2h desde T" desc="El camión lleva más de 2 horas de retraso en la llegada al origen. Acción inmediata." level="critical" />
                </tbody>
              </table>

              {/* Regla B */}
              <div className="px-3 py-2 border-t border-b bg-muted/10">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Regla B — demora en origen (configurable)</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Detecta cuando el camión está en el punto de carga hace demasiado tiempo sin partir.
                  Toma el <strong>último evento de origen</strong> — el más reciente entre
                  {" "}<Code>originArrivalAt</Code> (llegada al origen) y <Code>originLoadingAt</Code> (inicio de carga).
                  Si pasó demasiado tiempo desde ese evento y el envío sigue <Code>activo</Code>,
                  se dispara la alerta. Los umbrales se configuran en <Code>app.config.json</Code>{" "}
                  (clave <Code>shipments.originStaleYellowHours</Code> /{" "}
                  <Code>shipments.originStaleRedHours</Code>).
                </p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>

                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Tiempo transcurrido desde último evento origen</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Situación</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Alerta</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <Row cond="Sin llegada ni carga en origen" desc="No se registró ningún evento en origen todavía. La regla no aplica."                   level="none" />
                  <Row cond="Menos de 2h (configurable)"     desc="El camión sigue en origen, pero dentro del tiempo esperado de carga."                   level="none" />
                  <Row cond="Entre 2h y 5h (configurable)"   desc="Lleva más de 2 horas en origen sin partir. Verificar si hay demora en la carga."        level="alert" />
                  <Row cond="Más de 5h (configurable)"       desc="El camión lleva más de 5 horas en origen sin partir. Requiere intervención inmediata."  level="critical" />
                </tbody>
              </table>
            </div>

            {/* en_curso */}
            <div className="rounded-lg border overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 bg-orange-50 dark:bg-orange-950/20 border-b">
                <StatusDot color="bg-orange-500" />
                <span className="text-sm font-semibold">En curso</span>
                <span className="text-xs text-muted-foreground ml-1">— camión cargado, en tránsito o en destino</span>
              </div>
              <div className="px-3 py-2.5 border-b bg-muted/20">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  El estado <strong>En curso</strong> tiene <strong>dos reglas independientes</strong>. La de mayor severidad prevalece.
                </p>
              </div>

              {/* Regla A */}
              <div className="px-3 py-2 border-b bg-muted/10">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Regla A — llegada proyectada a destino</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Compara la <strong>llegada proyectada a destino</strong> (<Code>scheduledDestinationArrivalAt</Code>)
                  contra la <strong>llegada real registrada</strong> (<Code>destinationArrivalAt</Code>).
                  Solo aplica cuando el camión <strong>todavía no llegó</strong> al destino.
                </p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Tiempo vs. llegada proyectada (T)</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Situación</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Alerta</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <Row cond="Faltan más de 2h para T"   desc="El camión tiene tiempo suficiente para llegar al destino."                              level="none" />
                  <Row cond="Faltan menos de 2h para T" desc="Hora de anticipar la disponibilidad en destino y coordinar la descarga."                 level="alert" />
                  <Row cond="Pasó T, dentro de 2h"      desc="El camión no llegó todavía pero dentro del margen. Verificar con el chofer."             level="warning" />
                  <Row cond="Pasaron más de 2h desde T" desc="El camión lleva más de 2 horas de retraso en la llegada a destino. Acción inmediata."   level="critical" />
                </tbody>
              </table>

              {/* Regla B */}
              <div className="px-3 py-2 border-t border-b bg-muted/10">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Regla B — demora en destino sin descargar (configurable)</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Detecta cuando el camión ya llegó al destino pero no registró ni
                  {" "}<Code>destinationUnloadingAt</Code> (descarga en destino) ni <Code>finalizedAt</Code> (finalizado).
                  Mide el tiempo transcurrido desde <Code>destinationArrivalAt</Code>.
                  Los umbrales se configuran en <Code>app.config.json</Code>{" "}
                  (<Code>shipments.destinationStaleYellowHours</Code> /{" "}
                  <Code>shipments.destinationStaleRedHours</Code>).
                </p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Tiempo transcurrido desde llegada a destino</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Situación</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Alerta</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <Row cond="Sin llegada a destino"        desc="La regla no aplica hasta que se registre la llegada."                                              level="none" />
                  <Row cond="Menos de 2h (configurable)"   desc="El camión está en destino, dentro del tiempo esperado para descargar."                            level="none" />
                  <Row cond="Entre 2h y 5h (configurable)" desc="Lleva más de 2 horas en destino sin registrar descarga ni finalizar. Verificar situación."          level="alert" />
                  <Row cond="Más de 5h (configurable)"     desc="El camión lleva más de 5 horas en destino sin descargar ni finalizar. Requiere intervención."       level="critical" />
                </tbody>
              </table>
            </div>

          </Section>

          {/* ── Diagrama timeline ── */}
          <Section title="Línea de tiempo — Activo y En curso">
            <div className="rounded-lg border bg-muted/20 p-5 space-y-4">
              {/* barra */}
              <div className="flex items-center">
                <span className="text-[11px] text-muted-foreground w-28 text-right pr-3 shrink-0">T − 2h</span>
                <div className="flex flex-1 h-3 rounded-full overflow-hidden">
                  <div className="flex-1 bg-yellow-200 dark:bg-yellow-900/50" />
                  <div className="w-px bg-yellow-500" />
                  <div className="flex-1 bg-orange-300 dark:bg-orange-900/50" />
                  <div className="w-px bg-orange-500" />
                  <div className="flex-1 bg-red-400 dark:bg-red-900/60" />
                </div>
                <span className="text-[11px] text-muted-foreground w-10 text-right shrink-0">→</span>
              </div>
              {/* etiquetas de puntos */}
              <div className="flex items-start">
                <span className="w-28 shrink-0" />
                <div className="flex flex-1 text-[10px]">
                  <span className="flex-1 text-center text-yellow-700 dark:text-yellow-400 font-medium">🟡 Atención</span>
                  <span className="flex-1 text-center font-semibold text-muted-foreground">T<br/><span className="font-normal text-muted-foreground/60">llegada proyectada</span></span>
                  <span className="flex-1 text-center text-orange-700 dark:text-orange-400 font-medium">🟠 Demorado</span>
                  <span className="flex-1 text-center font-semibold text-muted-foreground">T+2h</span>
                  <span className="flex-1 text-center text-red-700 dark:text-red-400 font-medium">🔴 Crítico</span>
                </div>
                <span className="w-10 shrink-0" />
              </div>
              <p className="text-xs text-muted-foreground pt-1 border-t">
                La alerta desaparece automáticamente en cuanto se registra la llegada real — no requiere acción manual en el sistema.
              </p>
            </div>
          </Section>

          {/* ── Uso ── */}
          <Section title="Cómo usarlo">
            <p className="text-sm text-muted-foreground mb-3">
              Importar desde <Code>@cdc-sccr/shared</Code>. La función acepta cualquier objeto con
              los campos relevantes — los campos ausentes se tratan como <Code>null</Code>.
            </p>
            <pre className="rounded-lg border bg-muted/30 p-4 text-xs overflow-x-auto leading-relaxed">{`import {
  shipmentAlertLevel,
  ALERT_LEVEL_CARD_CLASSES,
  ALERT_LEVEL_BADGE_CLASSES,
  alertBadgeLabel,
  alertBadgeTooltip,
  type AlertLevel,
  type ShipmentAlertConfig,
} from "@cdc-sccr/shared"

// Fondo de card en Kanban (sin config custom — usa defaults 2h/5h)
const level = shipmentAlertLevel(row)  // "alert" | "warning" | "critical" | null

// Con umbrales desde app.config.json (Regla B configurable)
const level = shipmentAlertLevel(row, Date.now(), {
  originStaleYellowHours: appConfig?.shipments.originStaleYellowHours,
  originStaleRedHours:    appConfig?.shipments.originStaleRedHours,
})

<div className={cn("border rounded-xl", level ? ALERT_LEVEL_CARD_CLASSES[level] : "bg-card")}>

// Badge en header de columna
<span className={cn("rounded-full px-2 py-0.5 text-xs", ALERT_LEVEL_BADGE_CLASSES.warning)}>
  {alertBadgeLabel(count, status)}   // "2 demoradas" | "3 en alerta"
</span>

// Tooltip
<TooltipContent>{alertBadgeTooltip(status)}</TooltipContent>

// Tests — inyectar timestamp fijo como segundo parámetro
const level = shipmentAlertLevel(shipment, fixedNow, { originStaleYellowHours: 2, originStaleRedHours: 5 })`}</pre>
          </Section>

          {/* ── Exports ── */}
          <Section title="Exports del módulo">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left px-3 py-2 font-medium text-xs">Export</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Tipo</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Descripción</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    ["AlertLevel",                                     "type",     "\'alert\' | \'warning\' | \'critical\'"],
                    ["ShipmentAlertInput",                             "interface","Shape mínimo aceptado por shipmentAlertLevel()"],
                    ["ShipmentAlertConfig",                            "interface","Umbrales opcionales para Regla B (originStaleYellowHours / originStaleRedHours)"],
                    ["shipmentAlertLevel(shipment, now?, config?)",    "function", "Función principal — retorna AlertLevel | null"],
                    ["ALERT_LEVEL_CARD_CLASSES",                       "const",    "Clases Tailwind para el fondo de la card por nivel"],
                    ["ALERT_LEVEL_BADGE_CLASSES",                      "const",    "Clases Tailwind para badges por nivel"],
                    ["alertBadgeLabel(count, status)",                 "function", "Texto descriptivo del badge según columna y cantidad"],
                    ["alertBadgeTooltip(status)",                      "function", "Texto del tooltip según columna"],
                  ].map(([name, type, desc]) => (
                    <tr key={name}>
                      <td className="px-3 py-2 font-mono text-xs">{name}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{type}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ── Extensión ── */}
          <Section title="Cómo extender para nuevos estados">
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Agregar un <Code>case</Code> en el <Code>switch</Code> de <Code>shipmentAlertLevel()</Code> en <Code>packages/shared/src/utils/shipment-alerts.ts</Code>.</li>
              <li>Si el nuevo estado requiere un timestamp propio, agregarlo a <Code>ShipmentAlertInput</Code>.</li>
              <li>Actualizar <Code>alertBadgeTooltip()</Code> con el texto descriptivo para ese estado.</li>
              <li>Los componentes que consumen <Code>ALERT_LEVEL_CARD_CLASSES</Code> y <Code>alertBadgeLabel()</Code> se actualizan automáticamente — no hace falta tocarlos.</li>
            </ol>
            <p className="text-sm text-muted-foreground mt-2">
              Para ajustar las ventanas de tiempo fijas, modificar <Code>TWO_HOURS_MS</Code>, <Code>THIRTY_MIN_MS</Code> o <Code>SIXTY_MIN_MS</Code> al inicio del archivo.
              Para los umbrales de la Regla B (demora en origen), editar <Code>shipments.originStaleYellowHours</Code> y <Code>shipments.originStaleRedHours</Code> en <Code>app.config.json</Code> — sin reiniciar la API (caché 5 min).
            </p>
          </Section>

          {/* ── Consumidores ── */}
          <Section title="Consumidores actuales">
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 size-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                <span>
                  <Code>apps/web/src/pages/shipments/ShipmentsKanban.tsx</Code>
                  {" "}— fondo de card + badge "N demoradas / en alerta" en el header de las columnas
                  Planificado, Activo y En curso.
                </span>
              </li>
            </ul>
          </Section>

        </div>
      </div>
    </>
  )
}
